import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { VoucherScanLog } from "@/components/VoucherScanLog";
import { PageHeader, Card, StatusBadge, EmptyState } from "@/components/ui-helpers";
import { PendingApprovalBadge, TakeActionButton, pendingDocIds } from "@/components/PendingApprovalBadge";
import { useApprovalPendingMap } from "@/hooks/useApprovalPendingMap";
import { BranchFilter } from "@/components/BranchFilter";
import { VoucherNumberInput } from "@/components/VoucherNumberInput";
import { Plus, X, Trash2, Info, PackagePlus, Search } from "lucide-react";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import { ExportVoucherMenu } from "./IssueVoucherPage";
import { usePaginatedList } from "@/hooks/usePaginatedList";
import { PaginationControls } from "@/components/PaginationControls";
import { useFormTab } from "@/hooks/useFormTab";
import { ViewOverlay } from "@/hooks/useViewOverlay";
import { RefreshMastersButton } from "@/components/RefreshMastersButton";
import { useMastersRefresh } from "@/hooks/useMastersRefresh";
import { SearchableSelect } from "@/components/SearchableSelect";

const CONDITIONS = ["Good", "Damaged", "Needs Repair", "Lost"];

export default function ReturnVoucherPage() {
  const navigate = useNavigate();
  const [filterBranch, setFilterBranch] = useState("");
  // Sprint 4.3 — Warehouse filter (branch-scoped)
  const [filterWarehouse, setFilterWarehouse] = useState("");
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [search, setSearch] = useState("");
  const [viewing, setViewing] = useState(null);
  const [exporting, setExporting] = useState(null);

  const closeViewToList = () => setViewing(null);
  const closeViewToDashboard = () => { setViewing(null); navigate("/"); };

  const params = useMemo(() => (filterBranch ? { branch_id: filterBranch } : {}), [filterBranch]);
  const { items: list, total, page, pageSize, loading, setPage, setPageSize, reload } = usePaginatedList("/return-vouchers", params);
  const load = reload;
  const openFormTab = useFormTab();
  const pendingIds = useMemo(() => pendingDocIds(list), [list]);
  const pendingMap = useApprovalPendingMap("return_voucher", pendingIds);

  // Sprint 4.3 — Warehouse picker follows branch filter; reset on branch change.
  useEffect(() => {
    const url = filterBranch ? `/warehouses/picker?branch_id=${filterBranch}` : `/warehouses/picker`;
    api.get(url).then(r => setWarehouseOptions(Array.isArray(r.data) ? r.data : []))
       .catch(() => setWarehouseOptions([]));
    setFilterWarehouse("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBranch]);

  const openNew = () => openFormTab({
    path: "/return-vouchers/new",
    label: "New Return Voucher",
    icon: Plus,
    parent: "/return-vouchers",
    onSaved: load,
    render: ({ close, saved, viewList }) => <ReturnVoucherForm onViewList={viewList} onClose={close} onSaved={saved} />,
  });

  // Auto-spawn "New Return Voucher" tab on landing.
  const autoSpawnedRef = useRef(false);
  useEffect(() => {
    if (autoSpawnedRef.current) return;
    autoSpawnedRef.current = true;
    openNew();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    let base = list;
    if (q) {
      base = base.filter(v => [
        v.voucher_number, v.branch_name, v.to_warehouse_name, v.returned_from_name, v.returned_from_free_text,
        v.returned_from_type, v.status, (v.voucher_date || "").slice(0, 10),
      ].some(x => (x || "").toString().toLowerCase().includes(q)));
    }
    if (filterWarehouse) {
      base = base.filter(v => (v.to_warehouse_id || "") === filterWarehouse);
    }
    return base;
  }, [list, search, filterWarehouse]);

  return (
    <div>
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Stores"
        title="Material / Asset Return Voucher"
        subtitle="Reverse issued materials/assets — pick from the Returnable Register."
        actions={
          <button data-testid="new-rv-btn" onClick={openNew}
            className="bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold flex items-center gap-2">
            <Plus size={14} /> New Return Voucher
          </button>
        }
      />

      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="rv-search" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by voucher / returner / status…"
            className="border border-slate-300 bg-white pl-8 pr-3 py-2 text-sm focus:outline-none focus:border-[#F97316] min-w-[320px]" />
        </div>
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Branch</span>
        <BranchFilter value={filterBranch} onChange={setFilterBranch} testid="rv-branch-filter" />
        <span className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">Warehouse</span>
        <select data-testid="rv-warehouse-filter" value={filterWarehouse}
          onChange={e => setFilterWarehouse(e.target.value)}
          className="border border-slate-300 bg-white px-3 py-2 text-sm min-w-[180px]">
          <option value="">All warehouses</option>
          {warehouseOptions.map(w => (
            <option key={w.id} value={w.id}>{w.warehouse_code} — {w.warehouse_name}</option>
          ))}
        </select>
        <div className="ml-auto text-xs text-slate-500">
          <span className="text-slate-400 mr-1">Showing:</span>
          <span className="font-bold text-slate-800 tabular">{filteredList.length}</span>
          <span className="text-slate-400 mx-1">/</span>
          <span className="text-slate-600 tabular">{list.length}</span>
        </div>
      </div>

      {list.length === 0 ? <EmptyState title="No return vouchers yet" icon={PackagePlus} /> : filteredList.length === 0 ? <EmptyState title="No matches" hint="Clear the search to see all vouchers." /> : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="rv-table">
              <thead className="bg-slate-50 border-b-2 border-slate-200">
                <tr>{["Voucher No", "Date", "Branch", "Warehouse", "Returned From", "Lines", "Status", ""].map(h =>
                  <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-3 px-4">{h}</th>
                )}</tr>
              </thead>
              <tbody>
                {filteredList.map(v => (
                  <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="py-3 px-4 text-xs">
                      <button data-testid={`view-rv-${v.id}`} onClick={() => setViewing(v)}
                        className="font-mono font-bold text-[#F97316] hover:underline">
                        {v.voucher_number}
                      </button>
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600">{(v.voucher_date || "").slice(0, 10)}</td>
                    <td className="py-3 px-4 text-xs text-slate-600">{v.branch_name}</td>
                    <td className="py-3 px-4 text-xs text-slate-700" data-testid={`rv-wh-${v.id}`}>
                      {v.to_warehouse_name
                        ? <span className="font-bold">{v.to_warehouse_name}</span>
                        : <span className="text-amber-600">—</span>}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-800">
                      {v.returned_from_name || v.returned_from_free_text || "—"}
                      {v.returned_from_type && <div className="text-[10px] text-slate-400">{v.returned_from_type}</div>}
                    </td>
                    <td className="py-3 px-4 text-xs text-slate-600">{(v.line_items || []).length}</td>
                    <td className="py-3 px-4"><PendingApprovalBadge status={v.status} pendingInfo={pendingMap[v.id]} /></td>
                    <td className="py-3 px-4 text-right">
                      {pendingMap[v.id] && (
                        <span className="mr-2 inline-block align-middle">
                          <TakeActionButton pendingInfo={pendingMap[v.id]} testid={`take-action-rv-${v.id}`} />
                        </span>
                      )}
                      <button data-testid={`export-rv-${v.id}`}
                        onClick={(e) => {
                          const r = e.currentTarget.getBoundingClientRect();
                          // Anchor so popover bottom-right sits just above the Export button — consistent for any row.
                          setExporting({ ...v, _kind: "return", _anchor: { bottom: window.innerHeight - r.top + 6, right: window.innerWidth - r.right } });
                        }}
                        className="text-slate-500 hover:text-[#F97316] text-xs mr-3">Export</button>
                      <button onClick={() => setViewing(v)} className="text-slate-500 hover:text-[#F97316] text-xs">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <PaginationControls page={page} pageSize={pageSize} total={total} loading={loading} onPage={setPage} onPageSize={setPageSize} />
        </Card>
      )}

      {exporting && <ExportVoucherMenu voucher={exporting} kind={exporting._kind} anchor={exporting._anchor} onClose={() => setExporting(null)} />}
      {viewing && (
        <ViewOverlay testid="rv-view-overlay">
          <div className="bg-white w-full min-h-full">
            <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
              <div>
                <h2 className="font-display text-2xl font-bold">{viewing.voucher_number}</h2>
                <p className="text-xs text-slate-500 mt-0.5">{(viewing.voucher_date || "").slice(0, 10)} · {viewing.branch_name}</p>
              </div>
              <div className="flex items-center gap-2">
                <button type="button" onClick={closeViewToList}
                  className="bg-white border border-slate-300 px-4 py-2 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">
                  View List
                </button>
                <button onClick={closeViewToDashboard} className="p-1.5 hover:bg-slate-100" title="Back to Dashboard"><X size={18} /></button>
              </div>
            </div>
            <div className="px-8 py-6 space-y-5 max-w-[1600px] mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Returned From</div>
                  <div className="font-medium">{viewing.returned_from_name || viewing.returned_from_free_text || "—"}</div>
                  {viewing.returned_from_type && <div className="text-[10px] text-slate-500">{viewing.returned_from_type}</div>}
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Return To Warehouse</div>
                  <div className="font-medium">{viewing.to_warehouse_name || "—"}</div>
                </div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Reference</div><div className="font-medium">{viewing.reference_no || "—"}</div></div>
                <div><div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Status</div><StatusBadge status={viewing.status} /></div>
              </div>
              <VoucherScanLog vtype="rv" vid={viewing.id} />
              <Card className="overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>{["#", "Source Issue", "Type", "Item / Asset", "Return Qty", "Condition"].map(h =>
                      <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                    )}</tr>
                  </thead>
                  <tbody>
                    {(viewing.line_items || []).map((li, idx) => (
                      <tr key={idx} className="border-b border-slate-100">
                        <td className="py-2 px-3 text-xs">{idx + 1}</td>
                        <td className="py-2 px-3 text-xs font-mono">{li.issue_voucher_number}</td>
                        <td className="py-2 px-3"><span className={`text-[10px] font-bold uppercase ${li.line_type === "Material" ? "bg-emerald-50 text-emerald-800 px-2 py-0.5" : "bg-blue-50 text-blue-800 px-2 py-0.5"}`}>{li.line_type}</span></td>
                        <td className="py-2 px-3">{li.line_type === "Material" ? li.item_name : <><span className="font-mono text-xs font-bold">{li.asset_code}</span> · {li.item_name}</>}</td>
                        <td className="py-2 px-3 text-xs tabular">{li.line_type === "Material" ? `${li.return_qty} ${li.unit}` : "1"}</td>
                        <td className="py-2 px-3 text-xs">{li.condition_on_return}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            </div>
          </div>
        </ViewOverlay>
      )}
    </div>
  );
}

function ReturnVoucherForm({ onClose, onViewList, onSaved }) {
  const mastersRefreshKey = useMastersRefresh();
  const [branches, setBranches] = useState([]);
  // Sprint 4.3 Phase C — branch-scoped warehouse picker for "Return To Warehouse"
  const [warehouses, setWarehouses] = useState([]);
  const [members, setMembers] = useState([]);
  const [clients, setClients] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [register, setRegister] = useState([]);   // open returnable rows
  const [saving, setSaving] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  // Sprint 2026-Feb — factor out the initial form snapshot so we can
  // reset the form after a successful post without duplicating the
  // default object. Keeps the current voucher_date (today) so the user
  // doesn't have to re-pick "today" for the next entry.
  const emptyForm = () => ({
    voucher_date: today,
    branch_id: "", branch_name: "",
    // Sprint 4.3 Phase C — Return To Warehouse (mandatory)
    to_warehouse_id: "", to_warehouse_name: "",
    returned_from_type: "Member",
    returned_from_id: "", returned_from_name: "",
    returned_from_free_text: "",
    reference_no: "",
    notes: "",
    line_items: [],
  });
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    Promise.all([
      api.get("/branches"), api.get("/members/_pickable"),
      api.get("/clients/picker"), api.get("/vendors/picker"),
    ]).then(([b, m, c, v]) => {
      setBranches(b.data); setMembers(m.data);
      setClients(c.data); setVendors(v.data);
    });
  }, [mastersRefreshKey]);

  // Sprint 4.3 — Load warehouses scoped to selected branch.
  // Auto-picks the branch's default warehouse on first load + when branch changes.
  useEffect(() => {
    if (!form.branch_id) { setWarehouses([]); return; }
    api.get(`/warehouses/picker?branch_id=${form.branch_id}`).then(w => {
      const wlist = Array.isArray(w.data) ? w.data : [];
      setWarehouses(wlist);
      const cur = wlist.find(x => x.id === form.to_warehouse_id);
      if (!cur) {
        const dflt = wlist.find(x => x.is_default) || wlist[0];
        setForm(p => ({
          ...p,
          to_warehouse_id:   dflt?.id || "",
          to_warehouse_name: dflt?.warehouse_name || "",
        }));
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.branch_id, mastersRefreshKey]);

  // Load register filtered by BOTH branch + returned-from entity.
  // We deliberately require both filters before showing any returnables, so the user
  // is steered to the relevant items only and cannot accidentally return another entity's stock.
  useEffect(() => {
    if (!form.branch_id || !form.returned_from_id) { setRegister([]); return; }
    const params = new URLSearchParams({
      branch_id: form.branch_id,
      to_id: form.returned_from_id,
      to_type: form.returned_from_type,
    });
    api.get(`/returnable-register?${params}`).then(r => {
      setRegister((r.data || []).filter(x => x.status !== "Closed"));
    });
  }, [form.branch_id, form.returned_from_id, form.returned_from_type]);

  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const setBranch = (bid) => {
    const br = branches.find(b => b.id === bid);
    setForm(p => ({ ...p, branch_id: bid, branch_name: br?.branch_name || "",
                    // Sprint 4.3 — clear WH on branch change; useEffect will auto-pick default
                    to_warehouse_id: "", to_warehouse_name: "",
                    line_items: [] }));
  };

  const entityList = form.returned_from_type === "Member" ? members
                    : form.returned_from_type === "Client" ? clients
                    : form.returned_from_type === "Vendor" ? vendors : [];

  const setReturnedFromType = (t) => {
    setForm(p => ({ ...p, returned_from_type: t, returned_from_id: "", returned_from_name: "", returned_from_free_text: "", line_items: [] }));
  };
  const setReturnedFrom = (id) => {
    const ent = entityList.find(x => x.id === id);
    setForm(p => ({
      ...p, returned_from_id: id,
      returned_from_name: ent?.name || "",
      line_items: [],
    }));
  };

  function toggleRegister(rr) {
    const existing = form.line_items.findIndex(l => l.issue_voucher_id === rr.issue_voucher_id && l.issue_line_index === rr.issue_line_index);
    if (existing >= 0) {
      setForm(p => ({ ...p, line_items: p.line_items.filter((_, i) => i !== existing) }));
    } else {
      setForm(p => ({
        ...p, line_items: [...p.line_items, {
          issue_voucher_id: rr.issue_voucher_id,
          issue_voucher_number: rr.issue_voucher_number,
          issue_line_index: rr.issue_line_index,
          line_type: rr.line_type,
          item_id: rr.item_id, item_code: rr.item_code, item_name: rr.item_name, unit: rr.unit,
          batch_id: rr.batch_id, batch_code: rr.batch_code,
          asset_id: rr.asset_id, asset_code: rr.asset_code,
          return_qty: rr.line_type === "Material" ? rr.qty_pending : 1,
          unit_rate: rr.unit_rate || 0,
          condition_on_return: "Good",
          _pending: rr.qty_pending,
        }],
      }));
    }
  }

  function updLine(idx, patch) {
    setForm(p => ({ ...p, line_items: p.line_items.map((li, i) => i === idx ? { ...li, ...patch } : li) }));
  }
  function removeLine(idx) {
    setForm(p => ({ ...p, line_items: p.line_items.filter((_, i) => i !== idx) }));
  }

  const [rvSearch, setRvSearch] = useState("");
  const filteredRegister = register.filter(rr => {
    if (!rvSearch.trim()) return true;
    const q = rvSearch.trim().toLowerCase();
    return [rr.issue_voucher_number, rr.item_name, rr.item_code, rr.batch_code, rr.asset_code, rr.issued_to_name, rr.line_type]
      .some(v => (v || "").toString().toLowerCase().includes(q));
  });

  async function save(e) {
    e.preventDefault();
    if (!form.branch_id) { toast.error("Branch is mandatory"); return; }
    if (!form.to_warehouse_id) {
      toast.error("Return To Warehouse is mandatory. Pick a warehouse for the selected branch.");
      return;
    }
    if (form.line_items.length === 0) { toast.error("Pick at least one returnable item"); return; }
    for (let i = 0; i < form.line_items.length; i++) {
      const li = form.line_items[i];
      if (li.line_type === "Material") {
        if (Number(li.return_qty) <= 0) { toast.error(`Line ${i+1}: return qty must be > 0`); return; }
        if (Number(li.return_qty) > Number(li._pending) + 1e-6) { toast.error(`Line ${i+1}: cannot return more than ${li._pending} pending`); return; }
      }
    }
    setSaving(true);
    try {
      // Strip the _pending helper field before submit
      const payload = { ...form, line_items: form.line_items.map(({ _pending, ...rest }) => rest) };
      const res = await api.post("/return-vouchers", payload);
      // Sprint 2026-Feb — bug fix: user reported "no confirmation, page
      // remains as-is". Root causes were (a) global <Toaster> not
      // mounted (page-local Toasters were unmounted when the form tab
      // was active — fixed by adding a Toaster in Layout.jsx) and
      // (b) the form did not reset. We now surface the new voucher
      // number in the toast, refresh the list via onSaved(), and
      // reset the form to a blank state so the user can immediately
      // start the next voucher without re-opening anything.
      const num = res?.data?.voucher_number || "";
      toast.success(
        num ? `Return voucher ${num} posted` : "Return voucher posted",
        { duration: 4000 },
      );
      onSaved();
      setForm(emptyForm());
      setRegister([]);
      setRvSearch("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  }

  const pickedKey = (rr) => `${rr.issue_voucher_id}::${rr.issue_line_index}`;
  const isPicked = (rr) => form.line_items.some(l => l.issue_voucher_id === rr.issue_voucher_id && l.issue_line_index === rr.issue_line_index);

  return (
    <div className="bg-white -mt-6 lg:-mt-10 -mx-4 sm:-mx-6 lg:-mx-10 min-h-[calc(100vh-38px)]" data-testid="rv-form-overlay">
      <div className="bg-white w-full min-h-full">
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10 shadow-sm">
          <div>
            <h2 className="font-display text-2xl font-bold">New Return Voucher</h2>
            <p className="text-xs text-slate-500 mt-0.5">Pick open returnables from the register to reverse.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" data-testid="rv-view-list-btn" onClick={onViewList}
              className="bg-white border border-slate-300 px-4 py-2.5 text-xs uppercase tracking-wider font-bold text-slate-700 hover:bg-slate-50">
              View List
            </button>
            <RefreshMastersButton testid="rv-refresh-masters-btn" />
            <button type="button" data-testid="rv-close-btn" onClick={onClose} className="p-1.5 hover:bg-slate-100" title="Back to Dashboard"><X size={18} /></button>
          </div>
        </div>

        <form onSubmit={save} className="px-8 py-6 space-y-5 max-w-[1600px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <Field label="Voucher No (auto · admin can override)">
              <VoucherNumberInput
                module="return_voucher"
                value={form.voucher_number || ""}
                onChange={v => setF("voucher_number", v)}
                testidPrefix="rv-number"
              />
            </Field>
            <Field label="Voucher Date *">
              <input data-testid="rv-date" type="date" required value={form.voucher_date} onChange={e => setF("voucher_date", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Branch *">
              <select data-testid="rv-branch" required value={form.branch_id} onChange={e => setBranch(e.target.value)} className={inputCls}>
                <option value="">Select branch…</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
              </select>
            </Field>
            <Field label="Return To Warehouse *">
              <select
                data-testid="rv-to-warehouse"
                required
                disabled={!form.branch_id}
                value={form.to_warehouse_id || ""}
                onChange={e => {
                  const w = warehouses.find(x => x.id === e.target.value);
                  setForm(p => ({
                    ...p,
                    to_warehouse_id:   e.target.value,
                    to_warehouse_name: w?.warehouse_name || "",
                  }));
                }}
                className={inputCls}
              >
                <option value="">
                  {form.branch_id
                    ? (warehouses.length ? "Select warehouse…" : "No warehouse available")
                    : "Pick a branch first"}
                </option>
                {warehouses.map(w => (
                  <option key={w.id} value={w.id}>
                    {w.warehouse_code} — {w.warehouse_name}{w.is_default ? " · Default" : ""}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Reference No">
              <input value={form.reference_no} onChange={e => setF("reference_no", e.target.value)} className={inputCls} />
            </Field>
            <Field label="Notes">
              <textarea rows={1} value={form.notes} onChange={e => setF("notes", e.target.value)} className={inputCls} />
            </Field>
          </div>

          {/* Returned From group */}
          <div className="bg-slate-50/40 border border-slate-200 px-4 py-3">
            <h3 className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600 mb-3">Returned From</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Type">
                <select data-testid="rv-from-type" value={form.returned_from_type} onChange={e => setReturnedFromType(e.target.value)} className={inputCls}>
                  <option value="Member">Member</option>
                  <option value="Client">Client</option>
                  <option value="Vendor">Vendor</option>
                  <option value="Other">Other (free text)</option>
                </select>
              </Field>
              {form.returned_from_type !== "Other" ? (
                <Field label={form.returned_from_type} full>
                  {/* Sprint 2026-Feb — user asked for a search box inside
                      the Member / Client / Vendor picker (was a plain
                      <select>). SearchableSelect gives a filterable
                      popup that keeps only 8 rows visible + the rest
                      scrollable, matching the same UX used elsewhere
                      in Issue / Stock Adjust / Estimate flows. Branch-
                      scoped filtering is preserved. */}
                  <SearchableSelect
                    testid="rv-from-entity"
                    value={form.returned_from_id}
                    onChange={(id) => setReturnedFrom(id)}
                    items={entityList.filter(x => !form.branch_id || !x.branch_id || x.branch_id === form.branch_id)}
                    placeholder={`— All ${form.returned_from_type.toLowerCase()}s —`}
                    searchPlaceholder={`Search ${form.returned_from_type.toLowerCase()}s by name…`}
                    emptyText={`No matching ${form.returned_from_type.toLowerCase()}s`}
                    getLabel={(r) => r.name || ""}
                    getSubLabel={(r) => r.branch_name || r.code || ""}
                    getSearchText={(r) => `${r.name || ""} ${r.branch_name || ""} ${r.code || ""} ${r.email || ""} ${r.phone || ""}`}
                  />
                </Field>
              ) : (
                <Field label="Free Text" full>
                  <input data-testid="rv-from-free-text" value={form.returned_from_free_text}
                    onChange={e => setF("returned_from_free_text", e.target.value)} className={inputCls}
                    placeholder="Returner name" />
                </Field>
              )}
            </div>
          </div>

          {/* Open returnables picker */}
          <div className="border-t border-slate-200 pt-4">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div>
                <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500">
                  Open Returnables {register.length > 0 && <span className="text-slate-400 ml-2 normal-case font-normal">({filteredRegister.length} of {register.length} shown)</span>}
                </h3>
                {form.branch_id && form.returned_from_id && (
                  <div className="text-[10px] text-slate-500 mt-0.5">
                    Filtered to <span className="font-bold text-[#F97316]">{form.returned_from_name}</span> @ <span className="font-bold text-slate-700">{form.branch_name}</span>
                  </div>
                )}
              </div>
              {register.length > 0 && (
                <div className="relative w-96 max-w-full">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                  <input data-testid="rv-register-search" value={rvSearch} onChange={e => setRvSearch(e.target.value)}
                    placeholder="Search open returnables — voucher / item / batch / asset…"
                    className="border border-slate-300 focus:border-[#F97316] pl-8 pr-7 py-1.5 text-xs w-full focus:outline-none" />
                  {rvSearch && (
                    <button type="button" data-testid="rv-register-search-clear" onClick={() => setRvSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700">
                      <X size={13} />
                    </button>
                  )}
                </div>
              )}
            </div>
            {!form.branch_id ? (
              <p className="text-xs text-slate-500 italic flex items-center gap-1"><Info size={12} /> Select a branch first.</p>
            ) : (form.returned_from_type !== "Other" && !form.returned_from_id) ? (
              <div className="bg-amber-50 border border-amber-200 px-3 py-3 text-xs text-amber-900 flex items-center gap-2" data-testid="rv-needs-entity-hint">
                <Info size={14} className="shrink-0" />
                Pick the <strong>{form.returned_from_type}</strong> above to view their open returnables.
                Returnables are filtered to the chosen branch + {form.returned_from_type.toLowerCase()} only.
              </div>
            ) : form.returned_from_type === "Other" ? (
              <p className="text-xs text-slate-500 italic flex items-center gap-1"><Info size={12} /> Open returnables can only be listed for a registered Member / Client / Vendor.</p>
            ) : register.length === 0 ? (
              <EmptyState title="No open returnables for this branch + party" hint={`Nothing pending against the selected ${form.returned_from_type.toLowerCase()} at this branch.`} />
            ) : filteredRegister.length === 0 ? (
              <EmptyState title="No matches" hint="Clear the search to see all open returnables." />
            ) : (
              <Card className="overflow-hidden">
                <table className="w-full text-sm" data-testid="rv-register-table">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>{["", "Issue No", "Type", "Item / Asset", "Issued To", "Pending", "Exp. Return"].map(h =>
                      <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                    )}</tr>
                  </thead>
                  <tbody>
                    {filteredRegister.map(rr => (
                      <tr key={pickedKey(rr)} className={`border-b border-slate-100 cursor-pointer hover:bg-slate-50/60 ${isPicked(rr) ? "bg-orange-50/40" : ""}`} onClick={() => toggleRegister(rr)} data-testid={`rv-register-row-${rr.id}`}>
                        <td className="py-2 px-3"><input type="checkbox" readOnly checked={isPicked(rr)} className="accent-[#F97316]" /></td>
                        <td className="py-2 px-3 text-xs font-mono">{rr.issue_voucher_number}</td>
                        <td className="py-2 px-3"><span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${rr.line_type === "Material" ? "bg-emerald-50 text-emerald-800" : "bg-blue-50 text-blue-800"}`}>{rr.line_type}</span></td>
                        <td className="py-2 px-3">{rr.line_type === "Material" ? <>{rr.item_name} <span className="text-[10px] text-slate-400 font-mono">{rr.batch_code}</span></> : <><span className="font-mono text-xs font-bold">{rr.asset_code}</span> · {rr.item_name}</>}</td>
                        <td className="py-2 px-3 text-xs text-slate-700">
                          {rr.issued_to_name || "—"}
                          {rr.issued_to_type && <span className="text-[9px] text-slate-400 ml-1">[{rr.issued_to_type}]</span>}
                        </td>
                        <td className="py-2 px-3 text-xs tabular">{rr.qty_pending} {rr.unit}</td>
                        <td className={`py-2 px-3 text-xs ${rr.overdue ? "text-red-700 font-bold" : "text-slate-500"}`}>{rr.expected_return_date || "—"}{rr.overdue && " ⚠"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Card>
            )}
          </div>

          {/* Selected to return */}
          {form.line_items.length > 0 && (
            <div className="border-t border-slate-200 pt-4">
              <h3 className="text-xs uppercase tracking-[0.18em] font-bold text-slate-500 mb-3">To Return ({form.line_items.length})</h3>
              <div className="text-[11px] bg-blue-50 border border-blue-200 px-3 py-2 mb-3 leading-relaxed">
                <span className="font-bold text-blue-900 uppercase tracking-wider">Condition routing on post:</span>
                <span className="text-slate-700 ml-1"><b>Good</b> → adds back to stock / asset becomes <b>Available</b>. <b>Needs Repair</b> → goes to Repairs (zero-cost repair voucher); asset becomes <b>In Repair</b>; inventory qty restocks on Mark Ready. <b>Damaged</b> → asset goes to Repairs; inventory qty is written-off to the Scrap pool for scrap sale. <b>Lost</b> → asset becomes <b>Scrapped</b>; inventory qty is written-off — both land in Asset/Inventory Lifecycle → Scrap for scrap sale.</span>
              </div>
              <table className="w-full text-sm" data-testid="rv-lines-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>{["#", "Source", "Item / Asset", "Return Qty", "Condition", ""].map(h =>
                    <th key={h} className="text-left text-[10px] font-bold text-slate-500 uppercase tracking-wider py-2 px-3">{h}</th>
                  )}</tr>
                </thead>
                <tbody>
                  {form.line_items.map((li, idx) => (
                    <tr key={idx} className="border-b border-slate-100">
                      <td className="py-2 px-3 text-xs">{idx + 1}</td>
                      <td className="py-2 px-3 text-xs font-mono">{li.issue_voucher_number}</td>
                      <td className="py-2 px-3">{li.line_type === "Material" ? li.item_name : <><span className="font-mono text-xs font-bold">{li.asset_code}</span> · {li.item_name}</>}</td>
                      <td className="py-2 px-3 w-32">
                        {li.line_type === "Material" ? (
                          <input data-testid={`rv-qty-${idx}`} type="number" step="0.01" value={li.return_qty}
                            onChange={e => updLine(idx, { return_qty: e.target.value })}
                            max={li._pending}
                            className="w-full border border-slate-300 px-2 py-1.5 text-sm tabular text-right" />
                        ) : <span className="text-xs text-slate-400">1</span>}
                      </td>
                      <td className="py-2 px-3 w-32">
                        <select data-testid={`rv-cond-${idx}`} value={li.condition_on_return} onChange={e => updLine(idx, { condition_on_return: e.target.value })}
                          className="w-full border border-slate-300 px-2 py-1.5 text-sm">
                          {CONDITIONS.map(c => <option key={c}>{c}</option>)}
                        </select>
                      </td>
                      <td className="py-2 px-3">
                        <button type="button" onClick={() => removeLine(idx)} className="text-red-500 hover:text-red-700"><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex justify-end items-center gap-3 pt-4 border-t border-slate-200">
            {form.line_items.length === 0 && (
              <span className="text-[11px] text-slate-400 italic" data-testid="rv-save-hint">
                Tick at least one open returnable above to enable saving.
              </span>
            )}
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-slate-300 text-sm font-bold uppercase tracking-wider">Cancel</button>
            <button data-testid="save-rv-btn" type="submit"
              disabled={saving || form.line_items.length === 0}
              title={form.line_items.length === 0 ? "Select (tick) at least one returnable item first" : undefined}
              className="px-5 py-2.5 bg-[#F97316] text-white text-sm font-bold uppercase tracking-wider hover:bg-[#EA580C] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#F97316]">
              {saving ? "Saving…" : "Save & Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
